// View 2 — KB editor (full page).
//
// Per design/UI : UX v1.0/for-engineering-kb/specs/kb-strings.md.
// Routes: /admin/knowledge/base/new (create) + /admin/knowledge/base/:id (edit).
//
// Layout:
//   - Sticky page header: back link + title input + status pill + version chip
//     + "Version history" link
//   - Type selector (segmented control; document_excerpt disabled in create)
//   - Common meta row: domain / language / segment tags / contributor / version
//   - Subform area: type-discriminated. V1.0 ships qa_pair; framework /
//     case_study show V1.1 placeholders; document_excerpt is a read-only
//     review view (not reachable via create — only via row click).
//   - Sticky footer with status-aware buttons + autosave caption
//   - Inline confirm panels: Archive (warn) / Remove (danger) / Restore
//     (super_admin only; type-to-confirm)
//   - Version history side panel (read-only)
//
// RBAC: nav-gated by kb:read (super_admin + content_editor only). Within the
// editor, super_admin sees Restore; content_editor sees Restore disabled
// with tooltip.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Spinner,
} from '@gc-coach/design-system';
import type {
  CaseStudyBody,
  CaseStudyContext,
  DocumentExcerptBody,
  Domain,
  FrameworkBody,
  FrameworkPhase,
  KBEntry,
  KBEntryLanguage,
  KBEntryStatus,
  KBEntryType,
  QAPairBody,
} from '@gc-coach/shared';
import { ALL_DOMAINS, ALL_KB_ENTRY_TYPES } from '@gc-coach/shared';
import { AdminShell } from '../shell/index.js';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import {
  archiveKBEntry,
  createKBEntry,
  deleteKBEntry,
  duplicateKBEntry,
  getKBEntry,
  patchKBEntry,
  publishKBEntry,
  restoreKBEntry,
  reviewExcerpt,
  submitKBEntryForReview,
  unarchiveKBEntry,
} from '../api/endpoints.js';
import { formatRelativeTime } from '../users/time.js';
import { AIGlyph } from './AIGlyph.js';
import { AIVariantsPanel } from './AIVariantsPanel.js';
import styles from './KBEditor.module.css';

const LANGUAGES: ReadonlyArray<KBEntryLanguage> = ['en', 'fr'];

type ConfirmKind = null | 'archive' | 'remove' | 'restore';
type Mode = 'create' | 'edit';

// V1.0 body initial values per type
function initialBody(type: KBEntryType): QAPairBody | FrameworkBody | CaseStudyBody {
  if (type === 'qa_pair') return { question: '', question_variants: [], answer: '' };
  if (type === 'framework')
    return { methodology_name: '', when_to_apply: '', phases: [] };
  if (type === 'case_study')
    return {
      archetype: '',
      anonymized_context: {},
      situation: '',
      decision: '',
      outcome: '',
      lessons: [],
    };
  // document_excerpt isn't reachable from create; this is just to satisfy
  // the union (never used).
  throw new Error('document_excerpt cannot be initialized in create flow');
}

export function KBEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const mode: Mode = !id || id === 'new' ? 'create' : 'edit';
  const { t, tt } = useI18n();
  const { session } = useSession();
  const isSuperAdmin = session?.roles.includes('super_admin') ?? false;

  // ─── Entry state ──────────────────────────────────────────────────────
  // In `edit`, `entry` is loaded from the API. In `create`, it stays null
  // until first save (which transitions to `edit`).
  const [entry, setEntry] = useState<KBEntry | null>(null);
  const [isLoading, setIsLoading] = useState(mode === 'edit');
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editor local state (uncontrolled-style — we write through to the entry
  // on save).
  const [editType, setEditType] = useState<Exclude<KBEntryType, 'document_excerpt'>>(
    'qa_pair',
  );
  const [editDomain, setEditDomain] = useState<Domain>('D1_strategy_org');
  const [editLanguage, setEditLanguage] = useState<KBEntryLanguage>('en');
  const [editBody, setEditBody] = useState<
    QAPairBody | FrameworkBody | CaseStudyBody | DocumentExcerptBody
  >(initialBody('qa_pair'));

  // Confirm panel state
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [restoreConfirmText, setRestoreConfirmText] = useState('');

  // Version history panel
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  // Last-saved timestamp for the autosave caption
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // ─── Load entry on mount (edit mode) ──────────────────────────────────
  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const result = await getKBEntry(id);
      if (cancelled) return;
      if (result.ok) {
        const e = result.data.entry;
        setEntry(e);
        // Seed editor state from server values
        if (e.type !== 'document_excerpt') {
          setEditType(e.type);
        }
        setEditDomain(e.domain);
        setEditLanguage(e.language);
        setEditBody(e.body);
        setLastSavedAt(e.updatedAt);
      } else {
        setError(t('kb.editorGenericError'));
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, mode, t]);

  // ─── Save / lifecycle handlers ────────────────────────────────────────

  /**
   * Format a server VALIDATION_FAILED response as a reader-friendly banner.
   * The lifecycle completeness gate hands us `fieldErrors` keyed by dotted
   * path (`body.archetype`, `body.situation`); turn them into a short
   * "Required to publish: Archetype, Situation" sentence so the author
   * knows exactly which inputs to fill before retrying. Falls back to the
   * generic error for any error without field details.
   */
  function describeLifecycleError(result: { fieldErrors?: Record<string, string> }, action: 'review' | 'publish'): string {
    const fe = result.fieldErrors;
    if (!fe || Object.keys(fe).length === 0) return t('kb.editorGenericError');
    const labelFor = (key: string): string => {
      // Strip the `body.` prefix — the author doesn't care about the DB shape.
      const bare = key.startsWith('body.') ? key.slice(5) : key;
      // Map body keys to the sentence-case field label used in the subform.
      const map: Record<string, string> = {
        archetype: t('kb.fieldArchetype'),
        anonymized_context: t('kb.fieldAnonymizedContext'),
        situation: t('kb.fieldSituation'),
        decision: t('kb.fieldDecision'),
        outcome: t('kb.fieldOutcome'),
        lessons: t('kb.fieldLessons'),
        question: t('kb.fieldQuestion'),
        answer: t('kb.fieldAnswer'),
        methodology_name: t('kb.fieldMethodologyName'),
        when_to_apply: t('kb.fieldWhenToApply'),
        phases: t('kb.fieldPhases'),
      };
      return map[bare] ?? bare;
    };
    const names = Array.from(new Set(Object.keys(fe).map(labelFor))).join(', ');
    const key = action === 'publish' ? 'kb.editorRequiredToPublish' : 'kb.editorRequiredToSubmit';
    return tt(key, { fields: names });
  }

  async function saveAsDraft() {
    if (mode === 'create') {
      setActionPending(true);
      setError(null);
      // Build a properly-narrowed CreateKBEntryRequest by switching on
      // editType. The discriminated union requires the type+body pair to
      // be statically narrowed together; a generic object literal won't.
      const req =
        editType === 'qa_pair'
          ? {
              type: 'qa_pair' as const,
              domain: editDomain,
              language: editLanguage,
              body: editBody as QAPairBody,
            }
          : editType === 'framework'
            ? {
                type: 'framework' as const,
                domain: editDomain,
                language: editLanguage,
                body: editBody as FrameworkBody,
              }
            : {
                type: 'case_study' as const,
                domain: editDomain,
                language: editLanguage,
                body: editBody as CaseStudyBody,
              };
      const result = await createKBEntry(req);
      setActionPending(false);
      if (result.ok) {
        // Transition to edit mode by navigating to the new entry's URL.
        navigate(`/admin/knowledge/base/${result.data.entry.id}`, { replace: true });
      } else {
        setError(t('kb.editorGenericError'));
      }
    } else if (entry) {
      setActionPending(true);
      setError(null);
      const result = await patchKBEntry(entry.id, {
        domain: editDomain,
        language: editLanguage,
        body: editBody,
      });
      setActionPending(false);
      if (result.ok) {
        setEntry(result.data.entry);
        setLastSavedAt(result.data.entry.updatedAt);
      } else {
        setError(t('kb.editorGenericError'));
      }
    }
  }

  async function submitForReview() {
    if (!entry) return;
    setActionPending(true);
    setError(null);
    // Save body changes first, then transition.
    const patched = await patchKBEntry(entry.id, {
      domain: editDomain,
      language: editLanguage,
      body: editBody,
    });
    if (!patched.ok) {
      setActionPending(false);
      setError(t('kb.editorGenericError'));
      return;
    }
    const result = await submitKBEntryForReview(entry.id);
    setActionPending(false);
    if (result.ok) {
      setEntry(result.data.entry);
      setLastSavedAt(result.data.entry.updatedAt);
    } else {
      setError(describeLifecycleError(result, 'review'));
    }
  }

  async function publish() {
    if (!entry) return;
    setActionPending(true);
    setError(null);
    // Save body changes first so the published version includes the latest edits.
    const patched = await patchKBEntry(entry.id, {
      domain: editDomain,
      language: editLanguage,
      body: editBody,
    });
    if (!patched.ok) {
      setActionPending(false);
      setError(t('kb.editorGenericError'));
      return;
    }
    const result = await publishKBEntry(entry.id);
    setActionPending(false);
    if (result.ok) {
      setEntry(result.data.entry);
      setLastSavedAt(result.data.entry.updatedAt);
    } else {
      setError(describeLifecycleError(result, 'publish'));
    }
  }

  async function archive() {
    if (!entry) return;
    setConfirm(null);
    setActionPending(true);
    setError(null);
    const result = await archiveKBEntry(entry.id);
    setActionPending(false);
    if (result.ok) {
      setEntry(result.data.entry);
    } else {
      setError(t('kb.editorGenericError'));
    }
  }

  async function unarchive() {
    if (!entry) return;
    setActionPending(true);
    setError(null);
    const result = await unarchiveKBEntry(entry.id);
    setActionPending(false);
    if (result.ok) {
      setEntry(result.data.entry);
    } else {
      setError(t('kb.editorGenericError'));
    }
  }

  /** Clone the current entry (case_study only in V1) and navigate the
   *  editor to the fresh draft so the author can edit the differing
   *  fields immediately. */
  async function duplicate() {
    if (!entry) return;
    setActionPending(true);
    setError(null);
    const result = await duplicateKBEntry(entry.id);
    setActionPending(false);
    if (result.ok) {
      navigate(`/admin/knowledge/base/${result.data.entry.id}`);
    } else {
      setError(t('kb.editorGenericError'));
    }
  }

  async function remove() {
    if (!entry) return;
    const reason = removeReason.trim() || undefined;
    setConfirm(null);
    setRemoveReason('');
    setActionPending(true);
    setError(null);
    const result = await deleteKBEntry(entry.id, reason);
    setActionPending(false);
    if (result.ok) {
      // Soft-removed entry stays on screen; user can review or navigate back.
      setEntry(result.data.entry);
    } else {
      setError(t('kb.editorGenericError'));
    }
  }

  async function restore() {
    if (!entry || restoreConfirmText !== 'RESTORE') return;
    setConfirm(null);
    setRestoreConfirmText('');
    setActionPending(true);
    setError(null);
    const result = await restoreKBEntry(entry.id);
    setActionPending(false);
    if (result.ok) {
      setEntry(result.data.entry);
    } else if (result.code === 'FORBIDDEN') {
      setError(t('kb.editorRestoreForbidden'));
    } else {
      setError(t('kb.editorGenericError'));
    }
  }

  // ─── Derived ──────────────────────────────────────────────────────────

  const status: KBEntryStatus = entry?.status ?? 'draft';
  const version = entry?.version ?? 1;
  const isRemoved = status === 'removed';
  const isArchived = status === 'archived';

  // Title for the page-header input — derives from the body's main field per type
  const headerTitle = useMemo(() => {
    if (editType === 'qa_pair') return (editBody as QAPairBody).question;
    if (editType === 'framework') return (editBody as FrameworkBody).methodology_name;
    if (editType === 'case_study') return (editBody as CaseStudyBody).archetype;
    return entry?.title ?? '';
  }, [editType, editBody, entry]);

  // ─── Render ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <AdminShell
        activeKey="kb"
        breadcrumb={[t('kb.breadcrumbKnowledge'), t('kb.breadcrumbBase')]}
        state={{ kind: 'loading' }}
      >
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spinner />
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      activeKey="kb"
      breadcrumb={[
        t('kb.breadcrumbKnowledge'),
        t('kb.breadcrumbBase'),
        mode === 'create' ? t('kb.editorBreadcrumbNew') : t('kb.editorBreadcrumbEdit'),
      ]}
      state={{ kind: 'normal' }}
    >
      <div className={styles.page}>
        {/* Sticky header */}
        <header className={styles.pageHeader}>
          <div className={styles.pageHeaderLeft}>
            <button
              type="button"
              className={styles.backLink}
              onClick={() => navigate('/admin/knowledge/base')}
            >
              {t('kb.editorBackLink')}
            </button>
          </div>
          <div className={styles.pageHeaderRight}>
            <span className={`${styles.statusPill} ${statusPillClass(status, styles)}`}>
              {statusText(status, t)}
            </span>
            <span className={styles.versionChip}>v{version}</span>
            {mode === 'edit' && (
              <button
                type="button"
                className={styles.versionHistoryLink}
                onClick={() => setShowVersionPanel(true)}
              >
                {t('kb.editorVersionHistory')}
              </button>
            )}
          </div>
        </header>

        {/* Error banner */}
        {error && (
          <div style={{ margin: '12px 0' }}>
            <Alert severity="danger" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {/* Title field (full width below the sticky header) */}
        <div style={{ padding: '14px 0' }}>
          <input
            type="text"
            className={styles.pageTitleInput}
            placeholder={t('kb.editorTitlePlaceholder')}
            value={headerTitle}
            onChange={(e) => onTitleChange(e.target.value)}
            disabled={actionPending || isRemoved}
          />
        </div>

        {/* Type selector — segmented control. Disabled in edit mode (type is immutable). */}
        <div className={styles.typeSelectorWrap}>
          <span className={styles.typeSelectorLabel}>{t('kb.editorTypeLabel')}</span>
          <div className={styles.segmentedControl} role="tablist" aria-label="Entry type">
            {ALL_KB_ENTRY_TYPES.map((tp) => {
              const isCurrent = (entry ? entry.type : editType) === tp;
              const isExcerpt = tp === 'document_excerpt';
              // document_excerpt is disabled in CREATE (it comes from
              // ingestion, not authoring). In EDIT view of an excerpt, the
              // segment is the current — but it's still disabled (you can't
              // SWITCH to/from excerpt; type is immutable).
              const disabled =
                mode === 'create'
                  ? isExcerpt
                  : !isCurrent; // edit mode: only current type is "selected"; all others disabled
              return (
                <button
                  key={tp}
                  type="button"
                  className={[styles.segment, isCurrent ? styles.segmentActive : null]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={disabled || actionPending}
                  title={
                    isExcerpt && mode === 'create'
                      ? t('kb.editorExcerptTooltip')
                      : undefined
                  }
                  onClick={() => {
                    if (mode === 'create' && !isExcerpt) {
                      const newType = tp as Exclude<KBEntryType, 'document_excerpt'>;
                      setEditType(newType);
                      // Reset body to the new type's empty shape.
                      setEditBody(initialBody(newType));
                    }
                  }}
                >
                  {typeLabel(tp, t)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Common meta row */}
        <div className={styles.metaRow}>
          <div className={styles.metaField}>
            <label className={styles.metaLabel} htmlFor="kb-domain">
              {t('kb.editorMetaDomain')}
            </label>
            <select
              id="kb-domain"
              className={styles.metaSelect}
              value={editDomain}
              onChange={(e) => setEditDomain(e.target.value as Domain)}
              disabled={actionPending || isRemoved}
            >
              {ALL_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {t(`kb.domain.${d}`)}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.metaField}>
            <label className={styles.metaLabel} htmlFor="kb-language">
              {t('kb.editorMetaLanguage')}
            </label>
            <select
              id="kb-language"
              className={styles.metaSelect}
              value={editLanguage}
              onChange={(e) => setEditLanguage(e.target.value as KBEntryLanguage)}
              disabled={actionPending || isRemoved}
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.metaField}>
            <span className={styles.metaLabel}>{t('kb.editorMetaContributor')}</span>
            <span className={styles.metaReadonly}>{entry?.contributor ?? session?.displayName ?? ''}</span>
          </div>
          <div className={styles.metaField}>
            <span className={styles.metaLabel}>{t('kb.editorMetaVersion')}</span>
            <span className={styles.metaReadonly}>v{version}</span>
          </div>
        </div>

        {/* Subform area */}
        <div className={styles.subformCard}>
          {editType === 'qa_pair' && (
            <QaPairSubform
              body={editBody as QAPairBody}
              onChange={(b) => setEditBody(b)}
              disabled={actionPending || isRemoved}
              entryId={entry?.id}
              language={editLanguage}
            />
          )}
          {editType === 'framework' && (
            <FrameworkSubform
              body={editBody as FrameworkBody}
              onChange={(b) => setEditBody(b)}
              disabled={actionPending || isRemoved}
            />
          )}
          {editType === 'case_study' && (
            <CaseStudySubform
              body={editBody as CaseStudyBody}
              onChange={(b) => setEditBody(b)}
              disabled={actionPending || isRemoved}
            />
          )}
          {entry?.type === 'document_excerpt' && (
            <ExcerptReviewView
              entryId={entry.id}
              body={entry.body as DocumentExcerptBody}
              entryDomain={entry.domain}
              entryStatus={entry.status}
              disabled={actionPending}
              onReviewed={(e) => {
                setEntry(e);
                setLastSavedAt(e.updatedAt);
              }}
              onError={() => setError(t('kb.editorGenericError'))}
            />
          )}
        </div>

        {/* Inline confirm panels — same pattern as Tenants */}
        {confirm === 'archive' && (
          <InlineConfirm
            title={t('kb.confirmArchiveTitle')}
            body={t('kb.confirmArchiveBody')}
            confirmLabel={t('kb.confirmYes')}
            cancelLabel={t('kb.confirmCancel')}
            tone="warn"
            onConfirm={archive}
            onCancel={() => setConfirm(null)}
          />
        )}
        {confirm === 'remove' && (
          <RemoveConfirm
            title={t('kb.confirmRemoveTitle')}
            body={t('kb.confirmRemoveBody')}
            reasonLabel={t('kb.confirmRemoveReasonLabel')}
            reasonPlaceholder={t('kb.confirmRemoveReasonPlaceholder')}
            confirmLabel={t('kb.confirmYes')}
            cancelLabel={t('kb.confirmCancel')}
            reason={removeReason}
            setReason={setRemoveReason}
            onConfirm={remove}
            onCancel={() => {
              setConfirm(null);
              setRemoveReason('');
            }}
          />
        )}
        {confirm === 'restore' && (
          <RestoreConfirm
            title={t('kb.confirmRestoreTitle')}
            body={t('kb.confirmRestoreBody')}
            typeToConfirmLabel={t('kb.confirmRestoreTypeLabel')}
            expectedToken="RESTORE"
            confirmLabel={t('kb.confirmRestoreLabel')}
            cancelLabel={t('kb.confirmCancel')}
            value={restoreConfirmText}
            setValue={setRestoreConfirmText}
            onConfirm={restore}
            onCancel={() => {
              setConfirm(null);
              setRestoreConfirmText('');
            }}
          />
        )}
      </div>

      {/* Sticky footer with status-aware buttons */}
      <footer className={styles.stickyFooter}>
        <div className={styles.stickyFooterInner}>
          <span className={styles.autosaveCaption}>
            {lastSavedAt
              ? tt('kb.autosaveTemplate', { when: formatRelativeTime(lastSavedAt) })
              : t('kb.autosaveNever')}
          </span>
          <div className={styles.footerButtons}>
            {isRemoved ? (
              <>
                <Button kind="ghost" onClick={() => navigate('/admin/knowledge/base')}>
                  {t('kb.editorBackToList')}
                </Button>
                <Button
                  kind="ghost"
                  onClick={() => setConfirm('restore')}
                  disabled={actionPending || !isSuperAdmin}
                  title={isSuperAdmin ? undefined : t('kb.editorRestoreSuperAdminOnly')}
                >
                  {t('kb.editorRestore')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  kind="ghost"
                  onClick={() => navigate('/admin/knowledge/base')}
                  disabled={actionPending}
                >
                  {t('kb.editorCancel')}
                </Button>
                <Button kind="ghost" onClick={() => void saveAsDraft()} disabled={actionPending}>
                  {actionPending ? <Spinner /> : t('kb.editorSaveDraft')}
                </Button>
                {/* Duplicate — V1: case_study only (per Olivier
                  * 2026-10-22). Edit-mode only (needs an existing
                  * entry to clone). Clones + navigates to the new
                  * draft. */}
                {mode === 'edit' && entry?.type === 'case_study' && (
                  <Button
                    kind="ghost"
                    onClick={() => void duplicate()}
                    disabled={actionPending}
                    title={t('kb.editorDuplicateHint')}
                  >
                    {t('kb.editorDuplicate')}
                  </Button>
                )}
                {status === 'draft' && mode === 'edit' && (
                  <Button
                    kind="primary"
                    onClick={() => void submitForReview()}
                    disabled={actionPending}
                  >
                    {t('kb.editorSubmitForReview')}
                  </Button>
                )}
                {(status === 'in_review' || status === 'published') && (
                  <Button kind="primary" onClick={() => void publish()} disabled={actionPending}>
                    {t('kb.editorPublish')}
                  </Button>
                )}
                {status === 'published' && (
                  <Button
                    kind="ghost"
                    onClick={() => setConfirm('archive')}
                    disabled={actionPending}
                  >
                    {t('kb.editorArchive')}
                  </Button>
                )}
                {isArchived && (
                  <Button kind="ghost" onClick={() => void unarchive()} disabled={actionPending}>
                    {t('kb.editorUnarchive')}
                  </Button>
                )}
                {mode === 'edit' && (
                  <Button
                    kind="danger"
                    onClick={() => setConfirm('remove')}
                    disabled={actionPending}
                  >
                    {t('kb.editorRemove')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </footer>

      {/* Version history side panel (read-only in V1.0) */}
      {showVersionPanel && entry && (
        <>
          <div
            className={styles.versionPanelBackdrop}
            onClick={() => setShowVersionPanel(false)}
          />
          <aside className={styles.versionPanel}>
            <header className={styles.versionPanelHeader}>
              <span className={styles.versionPanelTitle}>{t('kb.editorVersionHistory')}</span>
              <button
                type="button"
                className={styles.versionPanelClose}
                onClick={() => setShowVersionPanel(false)}
                aria-label="Close version history"
              >
                ✕
              </button>
            </header>
            <p style={{ fontSize: 12, color: 'var(--ink3)' }}>
              {t('kb.editorVersionHistoryV1Note')}
            </p>
            <div className={styles.versionRow}>
              <span className={styles.versionRowVersion}>v{entry.version}</span>
              <div className={styles.versionRowMeta}>
                {statusText(entry.status, t)} · {entry.last_edited_by} ·{' '}
                {formatRelativeTime(entry.updatedAt)}
              </div>
            </div>
          </aside>
        </>
      )}
    </AdminShell>
  );

  // ─── Inline subcomponents using closure over state ────────────────────

  function onTitleChange(next: string) {
    // Title is a derived field — write back into the body's main field per type.
    if (editType === 'qa_pair') {
      setEditBody({ ...(editBody as QAPairBody), question: next });
    } else if (editType === 'framework') {
      setEditBody({ ...(editBody as FrameworkBody), methodology_name: next });
    } else if (editType === 'case_study') {
      setEditBody({ ...(editBody as CaseStudyBody), archetype: next });
    }
    // document_excerpt: title is derived from chapter/section; not editable here.
  }
}

// ─── qa_pair subform ─────────────────────────────────────────────────────

function QaPairSubform({
  body,
  onChange,
  disabled,
  entryId,
  language,
}: {
  body: QAPairBody;
  onChange: (b: QAPairBody) => void;
  disabled: boolean;
  entryId: string | undefined;
  language: KBEntryLanguage;
}) {
  const { t } = useI18n();
  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  function addVariant() {
    onChange({ ...body, question_variants: [...body.question_variants, ''] });
  }
  function updateVariant(i: number, value: string) {
    const next = [...body.question_variants];
    next[i] = value;
    onChange({ ...body, question_variants: next });
  }
  function removeVariant(i: number) {
    const next = body.question_variants.filter((_, idx) => idx !== i);
    onChange({ ...body, question_variants: next });
  }
  function acceptAiVariant(text: string) {
    onChange({ ...body, question_variants: [...body.question_variants, text] });
  }

  // AI trigger is enabled only when we have (a) a saved entry id, (b) a
  // non-empty answer (Claude needs grounding), and (c) the subform isn't
  // globally disabled. In create mode entryId is undefined — the button is
  // shown but disabled with an explanatory helper so editors know why.
  const answerFilled = body.answer.trim().length > 0;
  const aiEnabled = !disabled && Boolean(entryId) && answerFilled;
  const aiDisabledReason =
    !entryId
      ? t('kb.aiVariantsDisabledHelper')
      : !answerFilled
        ? t('kb.aiVariantsDisabledHelper')
        : '';

  return (
    <>
      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="kb-question">
          {t('kb.fieldQuestion')}
        </label>
        <input
          id="kb-question"
          type="text"
          className={styles.singleLineInput}
          value={body.question}
          onChange={(e) => onChange({ ...body, question: e.target.value })}
          disabled={disabled}
          placeholder={t('kb.fieldQuestionPlaceholder')}
        />
        <span className={styles.fieldHelper}>{t('kb.fieldQuestionHint')}</span>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel}>{t('kb.fieldQuestionVariants')}</label>
        <span className={styles.fieldHelper}>{t('kb.fieldQuestionVariantsHint')}</span>
        <div className={styles.variantsList} style={{ marginTop: 6 }}>
          {body.question_variants.map((v, i) => (
            <div key={i} className={styles.variantRow}>
              <input
                type="text"
                className={styles.singleLineInput}
                value={v}
                onChange={(e) => updateVariant(i, e.target.value)}
                disabled={disabled}
              />
              <button
                type="button"
                onClick={() => removeVariant(i)}
                disabled={disabled}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '4px 8px',
                  cursor: 'pointer',
                  color: 'var(--ink3)',
                }}
              >
                ✕
              </button>
            </div>
          ))}
          <div className={styles.variantsButtonRow}>
            <button
              type="button"
              className={styles.addVariantBtn}
              onClick={addVariant}
              disabled={disabled}
            >
              {t('kb.fieldAddVariant')}
            </button>
            <button
              type="button"
              className={styles.aiSuggestBtn}
              onClick={() => setAiPanelOpen(true)}
              disabled={!aiEnabled || aiPanelOpen}
            >
              <AIGlyph /> {t('kb.aiVariantsTrigger')}
            </button>
          </div>
          {!aiEnabled && aiDisabledReason && (
            <span className={styles.fieldHelper} style={{ marginTop: 4 }}>
              {aiDisabledReason}
            </span>
          )}
          {aiPanelOpen && entryId && (
            <AIVariantsPanel
              entryId={entryId}
              language={language}
              answerText={body.answer}
              excludeVariants={[
                body.question,
                ...body.question_variants.filter((v) => v.trim().length > 0),
              ]}
              onAccept={acceptAiVariant}
              onDismiss={() => setAiPanelOpen(false)}
            />
          )}
        </div>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="kb-answer">
          {t('kb.fieldAnswer')}
        </label>
        <textarea
          id="kb-answer"
          className={styles.richTextarea}
          value={body.answer}
          onChange={(e) => onChange({ ...body, answer: e.target.value })}
          disabled={disabled}
          placeholder={t('kb.fieldAnswerPlaceholder')}
        />
        <span className={styles.fieldHelper}>{t('kb.fieldAnswerHint')}</span>
      </div>
    </>
  );
}

// ─── framework subform ───────────────────────────────────────────────────

function FrameworkSubform({
  body,
  onChange,
  disabled,
}: {
  body: FrameworkBody;
  onChange: (b: FrameworkBody) => void;
  disabled: boolean;
}) {
  const { t } = useI18n();

  function updatePhase(i: number, next: FrameworkPhase) {
    const phases = body.phases.slice();
    phases[i] = next;
    onChange({ ...body, phases });
  }
  function removePhase(i: number) {
    onChange({ ...body, phases: body.phases.filter((_, idx) => idx !== i) });
  }
  function addPhase() {
    onChange({
      ...body,
      phases: [
        ...body.phases,
        { name: '', description: '', key_activities: [], deliverables: [] },
      ],
    });
  }

  return (
    <>
      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="kb-when-to-apply">
          {t('kb.fieldWhenToApply')}
        </label>
        <textarea
          id="kb-when-to-apply"
          className={styles.richTextarea}
          value={body.when_to_apply}
          onChange={(e) => onChange({ ...body, when_to_apply: e.target.value })}
          disabled={disabled}
          placeholder={t('kb.fieldWhenToApplyPlaceholder')}
        />
        <span className={styles.fieldHelper}>{t('kb.fieldWhenToApplyHint')}</span>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel}>{t('kb.fieldPhases')}</label>
        <span className={styles.fieldHelper}>{t('kb.fieldPhasesHint')}</span>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {body.phases.map((phase, i) => (
            <div key={i} className={styles.phaseCard}>
              <div className={styles.phaseCardHeader}>
                <span className={styles.phaseCardNumber}>
                  {t('kb.fieldPhaseNumberPrefix')} {i + 1}
                </span>
                <button
                  type="button"
                  className={styles.phaseCardRemove}
                  onClick={() => removePhase(i)}
                  disabled={disabled}
                  aria-label={t('kb.fieldRemovePhase')}
                >
                  ✕
                </button>
              </div>
              <input
                type="text"
                className={styles.singleLineInput}
                value={phase.name}
                onChange={(e) => updatePhase(i, { ...phase, name: e.target.value })}
                disabled={disabled}
                placeholder={t('kb.fieldPhaseNamePlaceholder')}
              />
              <textarea
                className={styles.richTextarea}
                style={{ minHeight: 80, marginTop: 8 }}
                value={phase.description}
                onChange={(e) => updatePhase(i, { ...phase, description: e.target.value })}
                disabled={disabled}
                placeholder={t('kb.fieldPhaseDescriptionPlaceholder')}
              />
              <StringList
                label={t('kb.fieldKeyActivities')}
                items={phase.key_activities}
                addLabel={t('kb.fieldAddActivity')}
                onChange={(items) => updatePhase(i, { ...phase, key_activities: items })}
                disabled={disabled}
              />
              <StringList
                label={t('kb.fieldDeliverables')}
                items={phase.deliverables}
                addLabel={t('kb.fieldAddDeliverable')}
                onChange={(items) => updatePhase(i, { ...phase, deliverables: items })}
                disabled={disabled}
              />
            </div>
          ))}
          <button
            type="button"
            className={styles.addVariantBtn}
            onClick={addPhase}
            disabled={disabled}
          >
            {t('kb.fieldAddPhase')}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── case_study subform ──────────────────────────────────────────────────

function CaseStudySubform({
  body,
  onChange,
  disabled,
}: {
  body: CaseStudyBody;
  onChange: (b: CaseStudyBody) => void;
  disabled: boolean;
}) {
  const { t } = useI18n();

  function setContext(field: keyof CaseStudyContext, value: string) {
    const next: CaseStudyContext = { ...body.anonymized_context };
    if (value.trim() === '') {
      delete next[field];
    } else {
      // geography is `string | string[]`; we treat the V1 input as a single string
      (next as Record<string, string>)[field] = value;
    }
    onChange({ ...body, anonymized_context: next });
  }

  const geography = Array.isArray(body.anonymized_context.geography)
    ? body.anonymized_context.geography.join(', ')
    : body.anonymized_context.geography ?? '';

  return (
    <>
      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel}>{t('kb.fieldAnonymizedContext')}</label>
        <span className={styles.fieldHelper}>{t('kb.fieldAnonymizedContextHint')}</span>
        <div className={styles.contextGrid}>
          <div className={styles.contextField}>
            <label className={styles.metaLabel} htmlFor="kb-context-sector">
              {t('kb.fieldSector')}
            </label>
            <input
              id="kb-context-sector"
              type="text"
              className={styles.singleLineInput}
              value={body.anonymized_context.sector ?? ''}
              onChange={(e) => setContext('sector', e.target.value)}
              disabled={disabled}
              placeholder={t('kb.fieldSectorPlaceholder')}
            />
          </div>
          <div className={styles.contextField}>
            <label className={styles.metaLabel} htmlFor="kb-context-revenue">
              {t('kb.fieldRevenueBand')}
            </label>
            <input
              id="kb-context-revenue"
              type="text"
              className={styles.singleLineInput}
              value={body.anonymized_context.revenue_band ?? ''}
              onChange={(e) => setContext('revenue_band', e.target.value)}
              disabled={disabled}
              placeholder={t('kb.fieldRevenueBandPlaceholder')}
            />
          </div>
          <div className={styles.contextField}>
            <label className={styles.metaLabel} htmlFor="kb-context-dept">
              {t('kb.fieldDeptSize')}
            </label>
            <input
              id="kb-context-dept"
              type="text"
              className={styles.singleLineInput}
              value={body.anonymized_context.dept_size ?? ''}
              onChange={(e) => setContext('dept_size', e.target.value)}
              disabled={disabled}
              placeholder={t('kb.fieldDeptSizePlaceholder')}
            />
          </div>
          <div className={styles.contextField}>
            <label className={styles.metaLabel} htmlFor="kb-context-geo">
              {t('kb.fieldGeography')}
            </label>
            <input
              id="kb-context-geo"
              type="text"
              className={styles.singleLineInput}
              value={geography}
              onChange={(e) => setContext('geography', e.target.value)}
              disabled={disabled}
              placeholder={t('kb.fieldGeographyPlaceholder')}
            />
          </div>
        </div>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="kb-situation">
          {t('kb.fieldSituation')}
        </label>
        <textarea
          id="kb-situation"
          className={styles.richTextarea}
          value={body.situation}
          onChange={(e) => onChange({ ...body, situation: e.target.value })}
          disabled={disabled}
          placeholder={t('kb.fieldSituationPlaceholder')}
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="kb-decision">
          {t('kb.fieldDecision')}
        </label>
        <textarea
          id="kb-decision"
          className={styles.richTextarea}
          value={body.decision}
          onChange={(e) => onChange({ ...body, decision: e.target.value })}
          disabled={disabled}
          placeholder={t('kb.fieldDecisionPlaceholder')}
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="kb-outcome">
          {t('kb.fieldOutcome')}
        </label>
        <textarea
          id="kb-outcome"
          className={styles.richTextarea}
          value={body.outcome}
          onChange={(e) => onChange({ ...body, outcome: e.target.value })}
          disabled={disabled}
          placeholder={t('kb.fieldOutcomePlaceholder')}
        />
      </div>

      <StringList
        label={t('kb.fieldLessons')}
        items={body.lessons}
        addLabel={t('kb.fieldAddLesson')}
        onChange={(items) => onChange({ ...body, lessons: items })}
        disabled={disabled}
      />
    </>
  );
}

// ─── document_excerpt review view ────────────────────────────────────────
// Read-only excerpt text + Approve / Re-tag / Reject actions. Reached by
// clicking an excerpt entry from the list. The cross-entry review *queue*
// (bulk inbox across all SourceDocuments) is V1.2 — depends on excerpts
// existing, which depends on the ingestion pipeline.

function ExcerptReviewView({
  entryId,
  body,
  entryDomain,
  entryStatus,
  disabled,
  onReviewed,
  onError,
}: {
  entryId: string;
  body: DocumentExcerptBody;
  entryDomain: Domain;
  entryStatus: KBEntryStatus;
  disabled: boolean;
  onReviewed: (e: KBEntry) => void;
  onError: () => void;
}) {
  const { t } = useI18n();
  const [retagOpen, setRetagOpen] = useState(false);
  const [retagDomain, setRetagDomain] = useState<Domain>(entryDomain);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [pending, setPending] = useState(false);

  const editorial = body.editorial_status ?? 'pending_review';
  // Once approved (→ published) or rejected (→ archived), hide actions —
  // status transitions out of the review queue. Restoring to pending is a
  // V1.2 concern; for now reviewer would re-open via Restore/Unarchive flow.
  const showActions = editorial === 'pending_review' && entryStatus !== 'removed';

  async function run(action: 'approve' | 'retag' | 'reject', extra?: { domain?: Domain; reason?: string }) {
    setPending(true);
    const result = await reviewExcerpt(entryId, { action, ...extra });
    setPending(false);
    if (result.ok) {
      onReviewed(result.data.entry);
      setRetagOpen(false);
      setRejectOpen(false);
      setRetagDomain(entryDomain);
      setRejectReason('');
    } else {
      onError();
    }
  }

  return (
    <div>
      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('kb.excerptSourceLabel')}</span>
        <div className={styles.metaReadonly}>
          {body.chapter} § {body.section}
          {body.page_range ? ` — ${body.page_range}` : ''}
        </div>
      </div>

      {body.suggested_domain && (
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>{t('kb.excerptSuggestedDomainLabel')}</span>
          <div className={styles.metaReadonly}>
            {t(`kb.domain.${body.suggested_domain}`)}
            {typeof body.confidence === 'number' && (
              <span style={{ marginLeft: 8, color: 'var(--ink3)' }}>
                · {Math.round(body.confidence * 100)}%
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.fieldGroup}>
        <span className={styles.fieldLabel}>{t('kb.excerptTextLabel')}</span>
        <div
          className={styles.metaReadonly}
          style={{
            background: 'var(--paper)',
            padding: 12,
            borderRadius: 6,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.5,
          }}
        >
          {body.text_chunk}
        </div>
      </div>

      {showActions && (
        <div className={styles.excerptActions}>
          <span className={styles.fieldLabel}>{t('kb.excerptReviewLabel')}</span>
          {!retagOpen && !rejectOpen && (
            <div className={styles.excerptActionRow}>
              <Button
                kind="primary"
                onClick={() => void run('approve')}
                disabled={disabled || pending}
              >
                {t('kb.excerptApprove')}
              </Button>
              <Button kind="ghost" onClick={() => setRetagOpen(true)} disabled={disabled || pending}>
                {t('kb.excerptRetag')}
              </Button>
              <Button kind="danger" onClick={() => setRejectOpen(true)} disabled={disabled || pending}>
                {t('kb.excerptReject')}
              </Button>
            </div>
          )}

          {retagOpen && (
            <div className={styles.excerptInlinePanel}>
              <label className={styles.metaLabel} htmlFor="kb-retag-domain">
                {t('kb.excerptRetagDomainLabel')}
              </label>
              <select
                id="kb-retag-domain"
                className={styles.metaSelect}
                value={retagDomain}
                onChange={(e) => setRetagDomain(e.target.value as Domain)}
                disabled={pending}
              >
                {ALL_DOMAINS.map((d) => (
                  <option key={d} value={d}>
                    {t(`kb.domain.${d}`)}
                  </option>
                ))}
              </select>
              <div className={styles.excerptActionRow} style={{ marginTop: 8 }}>
                <Button kind="ghost" onClick={() => setRetagOpen(false)} disabled={pending}>
                  {t('kb.confirmCancel')}
                </Button>
                <Button
                  kind="primary"
                  onClick={() => void run('retag', { domain: retagDomain })}
                  disabled={pending || retagDomain === entryDomain}
                >
                  {t('kb.excerptRetagConfirm')}
                </Button>
              </div>
            </div>
          )}

          {rejectOpen && (
            <div className={styles.excerptInlinePanel}>
              <label className={styles.metaLabel} htmlFor="kb-reject-reason">
                {t('kb.excerptRejectReasonLabel')}
              </label>
              <textarea
                id="kb-reject-reason"
                className={styles.confirmTextarea}
                rows={3}
                maxLength={500}
                value={rejectReason}
                placeholder={t('kb.excerptRejectReasonPlaceholder')}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={pending}
              />
              <div className={styles.excerptActionRow} style={{ marginTop: 8 }}>
                <Button kind="ghost" onClick={() => setRejectOpen(false)} disabled={pending}>
                  {t('kb.confirmCancel')}
                </Button>
                <Button
                  kind="danger"
                  onClick={() => {
                    const trimmed = rejectReason.trim();
                    void run('reject', trimmed ? { reason: trimmed } : {});
                  }}
                  disabled={pending}
                >
                  {t('kb.excerptRejectConfirm')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {editorial === 'approved' && (
        <div className={styles.excerptBadge} data-tone="ok">
          {t('kb.excerptApprovedNote')}
        </div>
      )}
      {editorial === 'rejected' && (
        <div className={styles.excerptBadge} data-tone="danger">
          {t('kb.excerptRejectedNote')}
        </div>
      )}
    </div>
  );
}

// ─── Shared: simple string-list repeater (add/remove rows) ───────────────

function StringList({
  label,
  items,
  addLabel,
  onChange,
  disabled,
}: {
  label: string;
  items: ReadonlyArray<string>;
  addLabel: string;
  onChange: (next: string[]) => void;
  disabled: boolean;
}) {
  function update(i: number, value: string) {
    const next = items.slice();
    next[i] = value;
    onChange(next);
  }
  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...items, '']);
  }
  return (
    <div className={styles.fieldGroup} style={{ marginTop: 8 }}>
      <label className={styles.fieldLabel}>{label}</label>
      <div className={styles.variantsList} style={{ marginTop: 6 }}>
        {items.map((v, i) => (
          <div key={i} className={styles.variantRow}>
            <input
              type="text"
              className={styles.singleLineInput}
              value={v}
              onChange={(e) => update(i, e.target.value)}
              disabled={disabled}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={disabled}
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '4px 8px',
                cursor: 'pointer',
                color: 'var(--ink3)',
              }}
              aria-label="Remove"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className={styles.addVariantBtn}
          onClick={add}
          disabled={disabled}
        >
          {addLabel}
        </button>
      </div>
    </div>
  );
}

// ─── Confirm panel components (mirror Tenants exactly) ───────────────────

function InlineConfirm({
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  tone: 'warn' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function RemoveConfirm({
  title,
  body,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  cancelLabel,
  reason,
  setReason,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  confirmLabel: string;
  cancelLabel: string;
  reason: string;
  setReason: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="kb-remove-reason">
        {reasonLabel}
      </label>
      <textarea
        id="kb-remove-reason"
        className={styles.confirmTextarea}
        rows={3}
        maxLength={500}
        value={reason}
        placeholder={reasonPlaceholder}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="danger" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function RestoreConfirm({
  title,
  body,
  typeToConfirmLabel,
  expectedToken,
  confirmLabel,
  cancelLabel,
  value,
  setValue,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  typeToConfirmLabel: string;
  expectedToken: string;
  confirmLabel: string;
  cancelLabel: string;
  value: string;
  setValue: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const enabled = value === expectedToken;
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="kb-restore-confirm">
        {typeToConfirmLabel}
      </label>
      <input
        id="kb-restore-confirm"
        type="text"
        className={styles.confirmInput}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="primary" onClick={onConfirm} disabled={!enabled}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function typeLabel(t: KBEntryType, i18n: (k: string) => string): string {
  if (t === 'qa_pair') return i18n('kb.typeQaPair');
  if (t === 'framework') return i18n('kb.typeFramework');
  if (t === 'case_study') return i18n('kb.typeCaseStudy');
  return i18n('kb.typeExcerpt');
}

function statusPillClass(s: KBEntryStatus, cls: Record<string, string>): string {
  if (s === 'draft') return cls['statusDraftPill'] ?? '';
  if (s === 'in_review') return cls['statusInReviewPill'] ?? '';
  if (s === 'published') return cls['statusPublishedPill'] ?? '';
  if (s === 'archived') return cls['statusArchivedPill'] ?? '';
  return cls['statusRemovedPill'] ?? '';
}

function statusText(s: KBEntryStatus, t: (k: string) => string): string {
  if (s === 'draft') return t('kb.statusDraft');
  if (s === 'in_review') return t('kb.statusInReview');
  if (s === 'published') return t('kb.statusPublished');
  if (s === 'archived') return t('kb.statusArchived');
  return t('kb.statusRemoved');
}
